/**
 * Global upload tracker. Anything in the app that uploads through
 * lib/upload.ts is automatically registered here, and the floating
 * UploadIndicator widget reflects the state in real time.
 *
 * Lifecycle of one upload:
 *   start({ id, label, kind })  → 'uploading'  (progress 0..1)
 *   updateProgress(id, 0.42)
 *   complete(id)                → marked done, auto-removed after a delay
 *   fail(id, message)           → marked errored, persists until user dismisses
 *
 * Aggregate getters power the collapsed pill ("3 uploading · 47%").
 */
import { create } from 'zustand';

export type UploadKind = 'image' | 'video' | 'thumbnail' | 'other';
export type UploadStatus = 'uploading' | 'finalizing' | 'done' | 'error';

export interface UploadEntry {
  id: string;
  label: string;
  kind: UploadKind;
  /** 0..1 — full upload + finalize progress. */
  progress: number;
  status: UploadStatus;
  /** Bytes total — used for ETA / display. */
  totalBytes: number;
  startedAt: number;
  errorMessage?: string;
  /** Optional local object-URL preview (revoked by the store on dismiss). */
  thumbUrl?: string;
}

interface UploadStore {
  uploads: Record<string, UploadEntry>;

  start: (e: Omit<UploadEntry, 'progress' | 'status' | 'startedAt'>) => void;
  updateProgress: (id: string, fraction: number) => void;
  setStatus: (id: string, status: UploadStatus) => void;
  complete: (id: string) => void;
  fail: (id: string, message?: string) => void;
  dismiss: (id: string) => void;
  dismissAllDone: () => void;

  // Selectors
  activeCount: () => number;
  hasActive: () => boolean;
}

export const useUploadStore = create<UploadStore>((set, get) => ({
  uploads: {},

  start: (e) =>
    set((s) => ({
      uploads: {
        ...s.uploads,
        [e.id]: {
          ...e,
          progress: 0,
          status: 'uploading',
          startedAt: Date.now(),
        },
      },
    })),

  updateProgress: (id, fraction) =>
    set((s) => {
      const u = s.uploads[id];
      if (!u || u.status === 'done' || u.status === 'error') return s;
      return {
        uploads: {
          ...s.uploads,
          [id]: { ...u, progress: Math.max(0, Math.min(1, fraction)) },
        },
      };
    }),

  setStatus: (id, status) =>
    set((s) => {
      const u = s.uploads[id];
      if (!u) return s;
      return { uploads: { ...s.uploads, [id]: { ...u, status } } };
    }),

  complete: (id) => {
    set((s) => {
      const u = s.uploads[id];
      if (!u) return s;
      return {
        uploads: { ...s.uploads, [id]: { ...u, status: 'done', progress: 1 } },
      };
    });
    // Auto-clear after a brief celebration window.
    setTimeout(() => get().dismiss(id), 2500);
  },

  fail: (id, message) =>
    set((s) => {
      const u = s.uploads[id];
      if (!u) return s;
      return {
        uploads: {
          ...s.uploads,
          [id]: { ...u, status: 'error', errorMessage: message },
        },
      };
    }),

  dismiss: (id) =>
    set((s) => {
      const next = { ...s.uploads };
      // Release the indicator's own preview object-URL (owned by the store).
      if (next[id]?.thumbUrl) URL.revokeObjectURL(next[id].thumbUrl as string);
      delete next[id];
      return { uploads: next };
    }),

  dismissAllDone: () =>
    set((s) => {
      const next: Record<string, UploadEntry> = {};
      for (const [k, v] of Object.entries(s.uploads)) {
        if (v.status !== 'done') next[k] = v;
        else if (v.thumbUrl) URL.revokeObjectURL(v.thumbUrl);
      }
      return { uploads: next };
    }),

  activeCount: () => {
    return Object.values(get().uploads).filter(
      (u) => u.status === 'uploading' || u.status === 'finalizing',
    ).length;
  },

  hasActive: () => get().activeCount() > 0,
}));
